/**
 * @description 编辑功能管理Hook
 * @date 2025-01-17
 * @author lv.chen
 */

import { useState } from 'react';

import { buriedBtnPoint } from '@home/utils/buriedPoint';

import type { IPublicFeedback } from '@intelligence/types/topCharts';

interface IUseFeedbackEditParams {
  /** 游戏ID */
  gameId?: string;
  /** 按钮打点配置 */
  btnLogKeyMap?: IRecord;
  /** 真数上传按钮打点配置的key */
  publicTrueDataReportBtnLogKey?: string;
}

interface IUseFeedbackEditReturn {
  /** 是否显示反馈弹窗 */
  isFeedbackModalVisible: boolean;
  /** 是否为编辑模式 */
  isEditMode: boolean;
  /** 正在编辑的记录 */
  editingRecord: Partial<IPublicFeedback>;
  /** 打开编辑弹窗 */
  openEdit: (record: IPublicFeedback) => void;
  /** 打开新增弹窗 */
  openAdd: () => void;
  /** 关闭反馈弹窗 */
  closeFeedbackModal: () => void;
}

/**
 * 编辑功能管理Hook
 */
export default function useFeedbackEdit(params: IUseFeedbackEditParams): IUseFeedbackEditReturn {
  const { gameId, btnLogKeyMap, publicTrueDataReportBtnLogKey } = params;

  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Partial<IPublicFeedback>>({});

  /**
   * 打开编辑弹窗
   */
  const openEdit = (record: IPublicFeedback): void => {
    setEditingRecord(record);
    setIsEditMode(true);
    setIsFeedbackModalVisible(true);
  };

  /**
   * 打开新增弹窗
   */
  const openAdd = (): void => {
    setEditingRecord({});
    setIsEditMode(false);
    setIsFeedbackModalVisible(true);

    // 埋点
    if (btnLogKeyMap && publicTrueDataReportBtnLogKey) {
      const publicTrueDataReportBtnLogConfig = btnLogKeyMap[publicTrueDataReportBtnLogKey];
      if (publicTrueDataReportBtnLogConfig) {
        const extInfo = publicTrueDataReportBtnLogConfig.extInfo || '';
        const extInfoNew = !gameId ? extInfo : `${extInfo}: (${gameId})`;
        buriedBtnPoint({ ...publicTrueDataReportBtnLogConfig, extInfo: extInfoNew });
      }
    }
  };

  /**
   * 关闭反馈弹窗
   */
  const closeFeedbackModal = (): void => {
    setIsFeedbackModalVisible(false);
  };

  return {
    isFeedbackModalVisible,
    isEditMode,
    editingRecord,
    openEdit,
    openAdd,
    closeFeedbackModal,
  };
}
